import {
  AnalyticsAuthEventType,
  AnalyticsChangeGiftsTabType,
  AnalyticsCipherDailyGameType,
  AnalyticsClaimRewardDailyDayType,
  AnalyticsClaimRewardDailyRewardType,
  AnalyticsClickToPlayType,
  AnalyticsClickerMenuButtonType,
  AnalyticsFaqQuestionButtonType,
  AnalyticsFaqQuestionType,
  AnalyticsFirstImprovementType,
  AnalyticsGiftType,
  AnalyticsLeaderboardButtonsType,
  AnalyticsLoadPageType,
  AnalyticsNewInvestGameButtonType,
  AnalyticsOnboardingUserType,
  AnalyticsRecalculationLeaguesType,
  AnalyticsScreenType,
  AnalyticsSettingsSwitchType,
  AnalyticsSettingsSwitchValueType,
  AnalyticsStartOnboardingType,
  AnalyticsTaskButtonsType,
  AnalyticsTaskType,
  analyticsFooterLinkType,
} from './TrackingService.constants';
import {ClientParams} from './TrackingService.types';
import {DI_TOKENS} from '@/di/di.tokens';

import {injected} from 'brandi';

import {AbstractTracking, createTracking} from '@specials/web-tracking';
import {EventData, EventName} from '@specials/web-tracking/lib/types';

import {PlatformService} from '@/services/PlatformService/PlatformService';
import {ClientRoutes, GlobalWindow} from '@/types';

export class TrackingService {
  private readonly tracking: AbstractTracking = createTracking();
  private clientParams: ClientParams = {
    specScreenType: undefined,
    specDbId: undefined,
  };

  constructor(
    private readonly globalWindow: GlobalWindow,
    private readonly routes: ClientRoutes,
    private readonly platformService: PlatformService,
  ) {}

  private getScreenType(): AnalyticsScreenType {
    if (this.platformService.isInvesting()) {
      return AnalyticsScreenType.NativeInvest;
    }

    if (this.platformService.isMb()) {
      return AnalyticsScreenType.Native;
    }

    return AnalyticsScreenType.Web;
  }

  private addClientParamsToEventData = (eventData?: EventData): EventData => {
    const extendedEventData = eventData ? {...eventData} : {};
    const {specScreenType, specDbId} = this.clientParams;

    extendedEventData.specScreenType = specScreenType;
    if (specDbId) {
      extendedEventData.specDbId = specDbId;
    }
    return extendedEventData;
  };

  private sendExtendedEvent = (eventName: EventName, eventData?: EventData): void => {
    this.tracking.sendEvent(eventName, this.addClientParamsToEventData(eventData));
  };

  public updateClientParams = (userId?: string): void => {
    this.clientParams = {specDbId: userId, specScreenType: this.getScreenType()};
  };

  public updateLocation = (): void => {
    this.tracking.updateLocation();

    // Для выполнения условия (в updateLocation трекаются редиректы):
    // отправляем при каждой загрузке каждой страницы одновременно с событием
    // pageload и в авторизованной зоне, и в неавторизованной зоне
    if (this.clientParams.specScreenType) {
      this.trackLoadPage();
    }
  };

  public subscribeTrackingModuleLoad = (callback: VoidFunction): void => {
    this.tracking.subscribeTrackingPageLoad(() => {
      // Сохраняем данные аналитики в Cookie. Данная механика описана по ссылке на вики:
      // @See: https://wiki.tcsbank.ru/x/BOZBcQ
      this.tracking.saveTrackingStateToCookie();

      callback();
    });
  };

  /** Трекинг авторизации */
  public trackAuthError(errorCode?: string): void {
    this.sendExtendedEvent('specAuth', {specVar: `${AnalyticsAuthEventType.AuthError}${errorCode || 'unknown_error'}`});
  }

  public trackAuthUserValid(): void {
    this.sendExtendedEvent('specAuth', {specVar: AnalyticsAuthEventType.AuthUserValid});
  }

  /** Трекинг загрузки страницы */
  public trackLoadPage = (): void => {
    const pathname = this.globalWindow.location.pathname;
    let specVar = '';
    if (pathname === this.routes.Main) {
      specVar = AnalyticsLoadPageType.Landing;
    } else if (pathname.split('/')[1] === this.routes.InvitePage.split('/')[1]) {
      specVar = AnalyticsLoadPageType.BringAFriend;
    }

    this.sendExtendedEvent('specAction1', {specVar});
  };

  /** Клик по кнопке "Играть" */
  public trackClickToPlayButton = (type: AnalyticsClickToPlayType): void => {
    this.sendExtendedEvent('specAction2', {specVar: type});
  };

  /** Клик по кнопке "Открыть в приложении" */
  public trackClickToFindAppButton = (): void => {
    this.sendExtendedEvent('specAction3');
  };

  /** Получение дейли награды */
  public trackClaimRewardDaily = (
    day: AnalyticsClaimRewardDailyDayType,
    reward: AnalyticsClaimRewardDailyRewardType,
  ): void => {
    this.sendExtendedEvent('specAction4', {specVar: day, specVar2: reward});
  };

  /** Старт онбординга */
  public trackStartOnboarding = (
    userType: AnalyticsOnboardingUserType,
    buttonType: AnalyticsStartOnboardingType,
  ): void => {
    this.sendExtendedEvent('specAction5', {specVar: userType, specVar2: buttonType});
  };

  /** Финиш онбординга */
  public trackFinishOnboarding = (userType: AnalyticsOnboardingUserType): void => {
    this.sendExtendedEvent('specAction6', {specVar: userType});
  };

  /** Клик по кнопкам меню и шапки в кликере */
  public trackClickToClickerMenuButton = (buttonType: AnalyticsClickerMenuButtonType): void => {
    this.sendExtendedEvent('specAction7', {specVar: buttonType});
  };

  /** Подзарядка батарейки */
  public trackRechargeEnergy = (): void => {
    this.sendExtendedEvent('specAction8');
  };

  /** Подзарядка батарейки */
  public trackCipherDailyGame = (type: AnalyticsCipherDailyGameType): void => {
    this.sendExtendedEvent('specAction9', {specVar: type});
  };

  /** Угаданный с первого раза шифр */
  public trackCipherDailyGameSuccess = (): void => {
    this.sendExtendedEvent('specAction10');
  };

  /** Получение награды за мини игру */
  public trackClaimRewardCipherDaily = (): void => {
    this.sendExtendedEvent('specAction11');
  };

  /** Клик по табам в призах */
  public trackChangeGiftsTab = (tabType: AnalyticsChangeGiftsTabType): void => {
    this.sendExtendedEvent('specAction12', {specVar: tabType});
  };

  /** Клик по кнопкам в призах */
  public trackClickToGiftButtons = (giftName: string, buttonText: string): void => {
    this.sendExtendedEvent('specAction13', {specVar: `${AnalyticsGiftType.Gift}${giftName}`, specVar2: buttonText});
  };

  /** Клик по первому улучшению */
  public trackClickToFirstImprovement = (type: AnalyticsFirstImprovementType): void => {
    this.sendExtendedEvent('specAction14', {specVar: type});
  };

  /** Клик по кнопкам в заданиях */
  public trackClickToTaskButtons = (taskName: string, buttonType: AnalyticsTaskButtonsType): void => {
    this.sendExtendedEvent('specAction15', {specVar: `${AnalyticsTaskType.Task}${taskName}`, specVar2: buttonType});
  };

  /** Клик по кнопкам в лигах */
  public trackClickToLeaderboardButtons = (
    leagueName: string | null,
    buttonType: AnalyticsLeaderboardButtonsType,
  ): void => {
    const eventData: EventData = {specVar2: buttonType};
    if (leagueName) {
      eventData.specVar = leagueName;
    }
    this.sendExtendedEvent('specAction16', eventData);
  };

  /** Переход в улучшения из лиг */
  public trackNavigateToImprovementsFromLeaderboard = (): void => {
    this.sendExtendedEvent('specAction17');
  };

  /** Клик по закрытым лигам */
  public trackClickToClosedLeagues = (clickedLeague: string, playerLeague: string | null): void => {
    const eventData: EventData = {specVar: clickedLeague};
    if (playerLeague) {
      eventData.specVar2 = playerLeague;
    }
    this.sendExtendedEvent('specAction18', eventData);
  };

  /** Экран перерасчета лиг */
  public trackRecalculationLeagues = (type: AnalyticsRecalculationLeaguesType): void => {
    this.sendExtendedEvent('specAction19', {specVar: type});
  };

  /** Переключение тогглов в настройках */
  public trackToggleSettingsSwitch = (type: AnalyticsSettingsSwitchType, value: boolean): void => {
    this.sendExtendedEvent('events.specAction20', {
      specVar: type,
      specVar2: value ? AnalyticsSettingsSwitchValueType.On : AnalyticsSettingsSwitchValueType.Off,
    });
  };

  /** Открытие ответа на вопрос в FAQ */
  public trackOpenFaqQuestion = (title: string): void => {
    this.sendExtendedEvent('events.specAction21', {
      specVar: AnalyticsFaqQuestionType.Block,
      specVar2: title,
    });
  };

  /** Клик по кнопке в ответе в FAQ */
  public trackClickToFaqQuestionButton = (buttonType: AnalyticsFaqQuestionButtonType): void => {
    this.sendExtendedEvent('events.specAction21', {
      specVar: AnalyticsFaqQuestionType.Button,
      specVar2: buttonType,
    });
  };

  /** Клик по ссылкам в подвале */
  public trackClickToFooterLinks = (title: string): void => {
    this.sendExtendedEvent('events.specAction21', {
      specVar: analyticsFooterLinkType,
      specVar2: title,
    });
  };

  /** Клик по ссылкам в подвале */
  public trackClickToNewInvestGameButton = (buttonType: AnalyticsNewInvestGameButtonType): void => {
    this.sendExtendedEvent('events.specAction22', {
      specVar: buttonType,
    });
  };

  public setPersonalizedLandingMetrics<T extends object>(personalizedLandingMetrics: T): void {
    const tracking = this.globalWindow.tracking;

    if (tracking) {
      tracking.state.personalizedLandingMetrics = {};
      tracking.state.personalizedLandingMetrics.state = {};
      Object.entries(personalizedLandingMetrics).forEach(([key, value]) => {
        tracking.state.personalizedLandingMetrics.state[key] = value;
      });
    }
  }
}

injected(TrackingService, DI_TOKENS.globalWindow, DI_TOKENS.clientRoutes, DI_TOKENS.platformService);
